import { BILLING_UNIT_CODES } from '../data/billingUnits.js'
import { LENDING_TERMS } from '../data/lendingTerms.js'

export const LENDING_TERM_VALUES = LENDING_TERMS.map((t) => t.value)

const URL_TO_DATA_KEY = Object.freeze({
    finance_amount: 'amount',
    lending_term: 'lending',
    planned_utilization: 'utilization',
    billing_unit: 'billing',
    locale: 'locale',
})

const DATA_URL_PARAMS = Object.freeze([
    'finance_amount',
    'lending_term',
    'planned_utilization',
    'billing_unit',
])

const URL_PARAMS = Object.keys(URL_TO_DATA_KEY)

export const FINANCE_AMOUNT_MIN = 1000
export const FINANCE_AMOUNT_MAX = 100_000_000
export const PLANNED_UTILIZATION_MIN = 1
export const PLANNED_UTILIZATION_MAX = 100_000_000
export const RESIDUAL_MIN = 1
export const RESIDUAL_MAX = 100_000_000

export function parseUrlPrefill(search) {
    const params = new URLSearchParams(search || '')
    const rawValues = {}
    let hasIntent = false
    for (const key of URL_PARAMS) {
        if (params.has(key)) {
            rawValues[key] = params.get(key)
            if (DATA_URL_PARAMS.includes(key)) hasIntent = true
            continue
        }
        rawValues[key] = null
    }
    return { hasIntent, rawValues }
}

export function validatePrefill(rawValues) {
    const data = {}
    const errors = {}
    for (const urlKey of URL_PARAMS) {
        const dataKey = URL_TO_DATA_KEY[urlKey]
        const result = validateField(urlKey, rawValues[urlKey])
        data[dataKey] = result.value
        if (result.error) errors[dataKey] = [result.error]
    }
    return { data, errors }
}

export function validateFinanceAmount(raw) {
    if (raw == null || raw === '') return { value: null, error: 'required' }
    const n = Number(raw)
    if (!Number.isFinite(n)) return { value: raw, error: 'must be numeric' }
    if (n < FINANCE_AMOUNT_MIN || n > FINANCE_AMOUNT_MAX) {
        return {
            value: raw,
            error: `must be between ${FINANCE_AMOUNT_MIN} and ${FINANCE_AMOUNT_MAX}`,
        }
    }
    return { value: raw, error: null }
}

export function validateLendingTerm(raw) {
    if (raw == null || raw === '') return { value: null, error: 'required' }
    if (!LENDING_TERM_VALUES.includes(raw)) {
        return {
            value: raw,
            error: `must be one of ${LENDING_TERM_VALUES.join(', ')}`,
        }
    }
    return { value: raw, error: null }
}

export function validatePlannedUtilization(raw) {
    if (raw == null || raw === '') return { value: null, error: 'required' }
    const n = Number(raw)
    if (!Number.isFinite(n)) return { value: raw, error: 'must be numeric' }
    if (n < PLANNED_UTILIZATION_MIN || n > PLANNED_UTILIZATION_MAX) {
        return {
            value: raw,
            error: `must be between ${PLANNED_UTILIZATION_MIN} and ${PLANNED_UTILIZATION_MAX}`,
        }
    }
    return { value: raw, error: null }
}

export function validateBillingUnit(raw) {
    if (raw == null || raw === '') return { value: null, error: 'required' }
    if (!BILLING_UNIT_CODES.includes(raw)) {
        return {
            value: raw,
            error: `must be one of ${BILLING_UNIT_CODES.join(', ')}`,
        }
    }
    return { value: raw, error: null }
}

const ALLOWED_LOCALES = Object.freeze(['en', 'de'])

function validateLocale(raw) {
    if (raw == null || raw === '') return { value: null, error: null }
    if (!ALLOWED_LOCALES.includes(raw)) {
        return { value: null, error: null }
    }
    return { value: raw, error: null }
}

const FIELD_VALIDATORS = Object.freeze(
    Object.assign(Object.create(null), {
        finance_amount: validateFinanceAmount,
        lending_term: validateLendingTerm,
        planned_utilization: validatePlannedUtilization,
        billing_unit: validateBillingUnit,
        locale: validateLocale,
    }),
)

function validateField(urlKey, raw) {
    const validator = FIELD_VALIDATORS[urlKey]
    return validator(raw)
}
